import React from "react";
import type { CostBreakdown } from "./types";
import { Skeleton } from "campaign-builder/ui";

interface CheckoutSummaryProps {
  /**
   * Null while the server-authoritative breakdown is loading
   * (drawer renders a skeleton). Non-null is the canonical
   * server-computed breakdown — never an FE approximation.
   */
  breakdown: CostBreakdown | null;
}

function formatCurrency(amount: number, currency: string): string {
  const code = currency.toUpperCase();
  const formatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: code,
    minimumFractionDigits: 2,
  });
  return formatter.format(amount);
}

// Design-system tokens matching the sibling OrderDetailsDrawer (shadcn/Tailwind):
// #222336 -> text-foreground, #626379 -> text-muted-foreground, #e2e2fc -> border-border.
const ROW = "flex items-center justify-between";
const LABEL = "text-foreground text-base font-medium leading-6";
const LABEL_MUTED = "text-muted-foreground text-base font-normal leading-6";
const VALUE = "text-foreground text-base font-semibold leading-6";
const VALUE_MUTED = "text-muted-foreground text-base font-normal leading-6";

export const CheckoutSummary: React.FC<CheckoutSummaryProps> = ({ breakdown }) => {
  if (!breakdown) {
    return (
      <>
        <div className="flex flex-col gap-4 border-t border-solid border-border py-4">
          <div className={ROW}>
            <span className={LABEL}>Subtotal</span>
            <Skeleton className="h-4 w-20" />
          </div>
          <div className={ROW}>
            <span className={LABEL_MUTED}>Card Processing Fee</span>
            <Skeleton className="h-4 w-20" />
          </div>
          <div className={ROW}>
            <span className={LABEL_MUTED}>Tax</span>
            <Skeleton className="h-4 w-20" />
          </div>
        </div>
        <div className="border-t border-solid border-border py-2">
          <div className={ROW}>
            <span className={LABEL}>Total due today</span>
            <Skeleton className="h-5 w-20" />
          </div>
        </div>
      </>
    );
  }

  const { subtotal, processingFee, tax, discount, total, currency, feePercentage, feeFlatFeeCents } = breakdown;
  const feeLabel =
    feePercentage != null && feeFlatFeeCents != null
      ? `Card Processing Fee (${feePercentage}% + ${(feeFlatFeeCents / 100).toFixed(2)})`
      : "Card Processing Fee";

  return (
    <>
      <div className="flex flex-col gap-4 border-t border-solid border-border py-4">
        <div className={ROW}>
          <span className={LABEL} data-test-id={`subtotalTitle`}>
            Subtotal
          </span>
          <span className={VALUE} data-test-id={`subtotalValue`}>
            {formatCurrency(subtotal, currency)}
          </span>
        </div>
        {discount > 0 && (
          <div className={ROW}>
            <span className={LABEL_MUTED} data-test-id={`discountTitle`}>
              Discount
            </span>
            <span className={VALUE_MUTED} data-test-id={`discountValue`}>
              -{formatCurrency(discount, currency)}
            </span>
          </div>
        )}
        <div className={ROW}>
          <span className={LABEL_MUTED} data-test-id={`cardProcessingFeeTitle`}>
            {feeLabel}
          </span>
          <span className={VALUE_MUTED} data-test-id={`cardProcessingFeeValue`}>
            {formatCurrency(processingFee, currency)}
          </span>
        </div>
        <div className={ROW}>
          <span className={LABEL_MUTED} data-test-id={`taxTitle`}>
            Tax
          </span>
          <span className={VALUE_MUTED} data-test-id={`taxValue`}>
            {formatCurrency(tax, currency)}
          </span>
        </div>
      </div>

      <div className="border-t border-solid border-border py-2">
        <div className={ROW}>
          <span className={LABEL} data-test-id={`totalDueTitle`}>
            Total due today
          </span>
          <span className={VALUE} data-test-id={`totalDueValue`}>
            {formatCurrency(total, currency)}
          </span>
        </div>
      </div>
    </>
  );
};
